import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ClipboardCheck, Compass, Sparkles, Video } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/Loader";

const HIGHLIGHTS = [
  [ClipboardCheck, "Un test di 2 minuti", "18 domande per scoprire il tuo archetipo tra 18 profili."],
  [Compass, "Il tuo percorso di 7 giorni", "Tre momenti al giorno pensati per te: mattino, mezzogiorno e sera."],
  [Sparkles, "Coach Mino, quando ti serve", "Il tuo coach virtuale legge le tue riflessioni e ti aiuta a ripartire nei giorni difficili."],
];

export default function Avvio() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user === null) return; // ancora in verifica
    if (user) {
      navigate(user.profile_id ? "/oggi" : "/test", { replace: true });
    }
  }, [user, navigate]);

  if (user === null) return <Loader label="Un momento…" />;
  if (user) return <Loader label="Ti porto al tuo percorso…" />;

  return (
    <div className="min-h-screen px-5 pb-12 pt-10" data-testid="avvio-page">
      <motion.img
        src="/logo-horizontal.png"
        alt="Coach2Go"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="mx-auto h-9 w-auto object-contain"
      />

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-10 text-center">
        <p className="eyebrow">Benvenuto in Coach2Go</p>
        <h1 className="font-serif-el mt-2 text-4xl font-semibold leading-[1.1] text-[#FAF9F6]" data-testid="avvio-title">
          Il tuo Coach<br /><span className="italic text-[#D4AF37]">sempre in tasca.</span>
        </h1>
        <p className="mx-auto mt-3 max-w-sm text-base leading-relaxed text-[#B4B4BC]">
          L'app è pronta. In due minuti scopri il tuo archetipo e da subito hai un passo concreto da fare, ogni giorno.
        </p>
      </motion.div>

      <div className="mt-8 space-y-3">
        {HIGHLIGHTS.map(([Icon, t, d], i) => (
          <motion.div
            key={t}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 + i * 0.08 }}
            className="flex gap-4 rounded-2xl border border-[#27272A] bg-[#121215] p-4"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#D4AF37]/12 text-[#D4AF37]">
              <Icon size={18} />
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg text-[#F4F4F5] font-semibold">{t}</h3>
              <p className="mt-0.5 text-base leading-snug text-[#B4B4BC]">{d}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mt-4 rounded-2xl border border-[#D4AF37]/50 bg-[#D4AF37]/[0.08] p-4" data-testid="avvio-annual-card">
        <div className="flex items-start justify-between gap-3">
          <p className="flex items-center gap-2 text-base font-semibold text-[#E5C158]"><Video size={18} className="shrink-0" /> Piano annuale</p>
          <span className="shrink-0 rounded-full bg-[#D4AF37] px-2.5 py-0.5 text-[11px] font-bold uppercase text-[#09090B]">Il più scelto</span>
        </div>
        <p className="mt-1.5 text-base leading-snug text-[#D4D4D8]">
          <b className="text-[#FAF9F6]">€49,99 all'anno</b> (circa €4,17 al mese) con <b className="text-[#E5C158]">4 webinar in video con Mino Cesari</b>, del valore di €80, inclusi.
        </p>
        <p className="mt-1.5 text-sm text-[#A1A1AA]">Prima provi gratis per 7 giorni, poi decidi tu.</p>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-8 space-y-3">
        <button
          onClick={() => navigate("/test")}
          data-testid="avvio-start-test"
          className="btn-gold flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-base uppercase tracking-wide"
        >
          Inizia il test gratuito <ArrowRight size={18} />
        </button>
        <button
          onClick={() => navigate("/accedi")}
          data-testid="avvio-login"
          className="w-full text-center text-sm font-medium text-[#8A8E96]"
        >
          Ho già un account · Accedi
        </button>
      </motion.div>
    </div>
  );
}
